import React, { useEffect, useState } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    IconButton, 
    Typography, 
    Grid, 
    Paper, 
    makeStyles, 
    createStyles, 
    Theme,
    Box
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

interface CityStatsChessboardProps {
    cityId: string;
    cityName: string;
    open: boolean;
    onClose: () => void;
}

// Definice typu pro položku odpadu
interface WasteItem {
    type: string;
    image: string;
    title: string;
}

const useStyles = makeStyles((theme: Theme) => createStyles({
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2),
    },
    closeButton: {
        color: theme.palette.grey[500],
    },
    wasteGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(2, 2),
    },
    wasteItem: {
        width: 200,
        textAlign: 'center',
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: theme.shadows[4],
        },
    },
    wasteImage: {
        width: '100%',
        height: 120,
        objectFit: 'cover',
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(1),
    },
    wasteTitle: {
        fontSize: '0.9rem',
        fontWeight: 'bold',
        lineHeight: 1.2,
        height: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    cityInfo: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
    }
}));

const CityStatsChessboard: React.FC<CityStatsChessboardProps> = ({ cityId, cityName, open, onClose }) => {
    const classes = useStyles();
    const [cityData, setCityData] = useState<{ name: string } | null>(null);
    const [loading, setLoading] = useState(true);

    // Data pro odpadové položky
    const wasteItems: WasteItem[] = [
        {
            type: 'I.O.1KO',
            image: 'https://www.envidata.cz/Web/ImagesNew/waste2_Thumb.jpg',
            title: 'Komunální odpad'
        },
        {
            type: 'I.O.2SKO',
            image: 'https://www.envidata.cz/Web/ImagesNew/waste_Mix_Thumb.jpg',
            title: 'Směsný komunální odpad'
        },
        {
            type: 'I.O.5SEPppsk',
            image: 'https://www.envidata.cz/Web/ImagesNew/waste_Recycling_Thumb.jpg',
            title: 'Tříděný odpad (papír, plast, sklo, kovy)'
        },
        {
            type: 'I.O.5SEPpap',
            image: 'https://www.envidata.cz/Web/ImagesNew/waste_Paper_Thumb.jpg',
            title: 'Papír'
        },
        {
            type: 'I.O.5SEPpl',
            image: 'https://www.envidata.cz/Web/ImagesNew/waste_Plastics_Thumb.jpg',
            title: 'Plasty'
        },
        {
            type: 'I.O.5SEPs',
            image: 'https://www.envidata.cz/Web/ImagesNew/waste_Glass_Thumb.jpg',
            title: 'Sklo'
        },
        {
            type: 'I.O.5SEPk',
            image: 'https://www.envidata.cz/Web/ImagesNew/waste_Metal_Thumb.jpg',
            title: 'Kovy'
        },
        {
            type: 'I.O.7.SEPBio',
            image: 'https://www.envidata.cz/Web/ImagesNew/waste_Bio_Thumb.jpg',
            title: 'Biologický odpad'
        },
        {
            type: 'I.O.4SD',
            image: 'https://www.envidata.cz/Web/ImagesNew/waste_Construction_Thumb.jpg',
            title: 'Stavební a demoliční odpad'
        },
        {
            type: 'I.O.8.SEPTex',
            image: 'https://www.envidata.cz/Web/ImagesNew/waste_Clothes_Thumb.jpg',
            title: 'Textilní odpad'
        },
        {
            type: 'I.O.3Obj',
            image: 'https://www.envidata.cz/Web/ImagesNew/waste_Large_Thumb.jpg',
            title: 'Objemný odpad'
        },
        {
            type: 'I.O.9SEPN',
            image: 'https://www.envidata.cz/Web/ImagesNew/waste_Hazard_Thumb.jpg',
            title: 'Nebezpečný odpad'
        }
    ];

    useEffect(() => {
        // Simulace načítání dat města podle cityId
        // V reálné aplikaci byste zde měli API volání pro získání dat
        const fetchCityData = async () => {
            setLoading(true);
            try {
                // Simulace API volání
                await new Promise(resolve => setTimeout(resolve, 800));
                
                // Simulovaná data - v reálné aplikaci by přišla z API
                setCityData({
                    name: cityName // Použijeme přímo cityName z props
                });
                
                console.log(`CityStatsChessboard - Loaded data for city: ${cityName} (ID: ${cityId})`);
            } catch (error) {
                console.error('Chyba při načítání dat města:', error);
            } finally {
                setLoading(false);
            }
        };

        if (open) {
            // Resetujeme stav při každém otevření nebo změně města
            setCityData(null);
            fetchCityData();
        }
    }, [cityId, cityName, open]); // Přidáno cityName do závislostí

    const handleWasteItemClick = (wasteType: string) => {
        console.log(`Kliknuto na odpad s typem: ${wasteType}`);
        // Otevření nového okna s odkazem na Envidata
        const url = `https://www.envidata.cz/dataAnalysis/odpady/detail.php?town=${cityId}&type=${wasteType}`;
        window.open(url, '_blank');
    };

    // Vykreslení mřížky s odpadovými položkami
    const renderWasteGrid = () => {
        return (
            <div className={classes.wasteGrid}>
                {wasteItems.map((item) => (
                    <Paper 
                        key={item.type} 
                        className={classes.wasteItem}
                        elevation={1}
                        onClick={() => handleWasteItemClick(item.type)}
                    >
                        <img 
                            src={item.image} 
                            alt={item.title} 
                            className={classes.wasteImage}
                        />
                        <Typography className={classes.wasteTitle}>
                            {item.title}
                        </Typography>
                    </Paper>
                ))}
            </div>
        );
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="lg" 
            fullWidth
            aria-labelledby="city-stats-dialog-title"
        >
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <Typography variant="h6" id="city-stats-dialog-title">
                    Statistiky odpadů pro {cityName}
                </Typography>
                <IconButton 
                    aria-label="close" 
                    className={classes.closeButton} 
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {renderWasteGrid()}
            </DialogContent>
            <Box 
                textAlign="right" 
                p={2} 
                borderTop="1px solid rgba(0, 0, 0, 0.12)"
            >
                <Typography variant="caption" color="textSecondary">
                    Zdroj datových vizualizací: <strong 
                        style={{ 
                            cursor: 'pointer',
                            textDecoration: 'underline'
                        }}
                        onClick={() => window.open('https://www.envidata.cz/', '_blank')}
                    >
                        envidata.cz
                    </strong>
                </Typography>
            </Box>
        </Dialog>
    );
};

export default CityStatsChessboard;